import type { Decidable, DecisionState } from "./decision-state.js";

/**
 * The seven steps of a cycle, in order.
 *
 * Cycle 1 builds the app and every later cycle changes it, running these same
 * seven steps focused only on what is new or changed. There is no separate
 * "change" flow to keep in sync with this one.
 */
export const CYCLE_STEPS = [
  "describe",
  "clarify",
  "backlog",
  "prototype",
  "plan",
  "build",
  "review",
] as const;

export type CycleStep = (typeof CYCLE_STEPS)[number];

/**
 * Which decision states stop a step being complete.
 *
 * **This table is the whole reason completion is derived rather than stored.**
 * A `stepComplete` flag would have to be recalculated at every one of the
 * dozens of places a decision changes, and the first one that forgets leaves
 * the product claiming a step is finished when it is not.
 *
 * The rows are not uniform, and the differences are load-bearing:
 *
 * - **prototype** blocks on `rejected` as well as `waiting` (`PRO-006-AC1`:
 *   "unavailable while any story is waiting or rejected"). A rejection at
 *   step 4 is an instruction to regenerate, so the step is not finished — it
 *   has work outstanding.
 * - **review** deliberately does *not* block on `rejected` (`REV-005-AC2`:
 *   releasing is available with rejected tests outstanding, and says which are
 *   being carried into the backlog). A rejection at step 7 is routed into the
 *   next cycle by `REV-004-AC3`, so it is settled here even though it is not
 *   resolved.
 *
 * Getting those two backwards would either strand a user at step 4 with no way
 * forward, or let them release a version whose rejections were silently
 * dropped.
 *
 * **backlog blocks on `rejected` even though nothing can produce it.** The
 * backlog offers approve, edit and delete but no reject (`BLG-005`), so the
 * state is unreachable today. It is listed anyway because the honest failure
 * direction is refusing to continue on a state we did not expect, rather than
 * treating an unexplained value as consent.
 */
const BLOCKING_STATES: Readonly<Record<CycleStep, readonly DecisionState[]>> = {
  describe: ["waiting", "rejected"],
  clarify: ["waiting", "rejected"],
  backlog: ["waiting", "rejected"],
  prototype: ["waiting", "rejected"],
  plan: ["waiting", "rejected"],
  build: ["waiting", "rejected"],
  review: ["waiting"],
};

export interface StepCompletion {
  readonly step: CycleStep;
  readonly complete: boolean;
  /** Settled out of total — `PRODUCT.md` principle 7, "progress is a number". */
  readonly settled: number;
  readonly total: number;
  /**
   * The items holding the step open, in the order they were given.
   *
   * Returned rather than merely counted because every gate in the product has
   * to say *which* items are outstanding, not just how many
   * (`BLG-006-AC1`, `PRO-006-AC1`).
   */
  readonly outstanding: readonly string[];
}

/**
 * Whether a step is finished, derived from the items it governs.
 *
 * Never store this. A step is complete when nothing it governs is outstanding,
 * and that is a function of the decisions — the moment it becomes a field, it
 * can disagree with them, and the product's central promise goes with it.
 *
 * A step governing nothing is complete. That is not a degenerate case to guard
 * against: a change cycle that touches no stories at a given step genuinely has
 * nothing to decide there, and blocking on an empty list would deadlock it.
 */
export const stepCompletion = (
  step: CycleStep,
  governed: readonly Decidable[],
): StepCompletion => {
  const blocking = BLOCKING_STATES[step];
  const outstanding = governed
    .filter((item) => blocking.includes(item.state))
    .map((item) => item.id);

  return {
    step,
    complete: outstanding.length === 0,
    settled: governed.length - outstanding.length,
    total: governed.length,
    outstanding,
  };
};

/**
 * Whether a state settles an item at a given step.
 *
 * The same question `stepCompletion` answers in bulk, exposed for the single
 * item case so no caller has to re-derive the per-step rule and get the step-4
 * and step-7 asymmetry wrong.
 */
export const isSettledAt = (step: CycleStep, state: DecisionState): boolean =>
  !BLOCKING_STATES[step].includes(state);
